// src/oura/components/newscan/SlotText.tsx
//
// Question text shown read-only, with every blank word (`{price_point}`, a
// word with no value yet) drawn as a blank: a grey "…" on a dotted underline,
// and "<word label> needed" for a screen reader. Text with no blank renders
// as it is. The editor's field keeps the raw token as typed.

import { Fragment, type ReactNode } from "react";
import type { ScanDraftWord } from "@service/aeo/scanDraftTypes";
import { SLOT_TEXT, slotParts } from "@oura/newscan/copy";

/** The label for a word when the draft's words are not at hand: "price_point" -> "Price point". */
const fallbackLabel = (variable: string) => {
    const s = variable.replace(/_/g, " ");
    return s.charAt(0).toUpperCase() + s.slice(1);
};

/** One blank, drawn in place of its token. */
export function SlotBlank({ variable, label }: { variable: string; label?: string }) {
    return (
        <span className="text-gray-400 underline decoration-gray-400 decoration-dotted decoration-2 underline-offset-[3px]">
            <span aria-hidden>{SLOT_TEXT.blank}</span>
            <span className="sr-only">{SLOT_TEXT.needed(label ?? fallbackLabel(variable))}</span>
        </span>
    );
}

export interface SlotTextProps {
    text: string;
    /** The draft's words by variable, for the blank's label. */
    words?: Record<string, ScanDraftWord>;
    /** Renders a plain run (e.g. to mark a value inside it). Default: the text as it is. */
    renderText?: (text: string) => ReactNode;
}

export function SlotText({ text, words, renderText }: SlotTextProps) {
    const parts = slotParts(text);
    return (
        <>
            {parts.map((p, i) =>
                p.variable === null ? (
                    <Fragment key={i}>{renderText ? renderText(p.text) : p.text}</Fragment>
                ) : (
                    <SlotBlank key={i} variable={p.variable} label={words?.[p.variable]?.label} />
                ),
            )}
        </>
    );
}
